import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import InputV2 from '../InputV2'
import MinutesInput from '../MinutesInput'

const TimePickerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const TimeSeparator = styled.span`
  color: #011533;
  font-size: 16px;
  font-weight: 400;
  margin: 0 2px;
`

const AMPMToggle = styled.div`
  display: flex;
  border: ${props => (props.error ? '1px solid red' : '1px solid #e2e2fc')};
  border-radius: 6px;
  overflow: hidden;
  height: 32px;
`

const AMPMButton = styled.button`
  border: none;
  background: ${props =>
    props.error && props.active ? 'red' : props.active ? '#e2e2fc' : 'white'};
  color: ${props => (props.active ? 'black' : '#707087')};
  font-weight: ${props => (props.active ? '700' : '400')};
  font-size: 14px;
  padding: 0 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => (props.active ? '#e2e2fc' : '#f5f5f5')};
  }
`

const TimePickerV2 = ({ value, onChange = () => {}, placeholders, error }) => {
  const [time, setTime] = useState(() => {
    if (value) {
      const match = value.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)
      if (match) {
        return {
          hours: parseInt(match[1], 10),
          minutes: parseInt(match[2], 10),
          period: match[3].toUpperCase(),
        }
      }
    }
    return { hours: null, minutes: null, period: null }
  })

  useEffect(() => {
    if (value) {
      const match = value.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)
      if (match) {
        setTime({
          hours: parseInt(match[1], 10),
          minutes: parseInt(match[2], 10),
          period: match[3].toUpperCase(),
        })
      }
    } else {
      setTime({
        hours: null,
        minutes: null,
        period: placeholders.period || null,
      })
    }
  }, [value])

  const handleHoursChange = newHours => {
    const updatedTime = { ...time, hours: newHours }
    setTime(updatedTime)
    formatAndEmitTime(updatedTime)
  }

  const handleMinutesChange = newMinutes => {
    const updatedTime = { ...time, minutes: newMinutes }
    setTime(updatedTime)
    formatAndEmitTime(updatedTime)
  }

  const handlePeriodChange = newPeriod => {
    const updatedTime = { ...time, period: newPeriod }
    setTime(updatedTime)
    formatAndEmitTime(updatedTime)
  }

  const formatAndEmitTime = timeObj => {
    // Only emit formatted time if all parts are filled
    if (timeObj.hours !== null && timeObj.minutes !== null && timeObj.period) {
      const formattedTime = `${timeObj.hours
        .toString()
        .padStart(2, '0')}:${timeObj.minutes.toString().padStart(2, '0')} ${
        timeObj.period
      }`
      onChange(formattedTime)
    } else if (value && value.trim() !== '') {
      // Only emit empty string if we previously had a valid time that's now incomplete
      // This prevents the useEffect reset when user is just setting AM/PM initially
      onChange('')
    }
    // Don't emit empty string for partial selections to avoid triggering useEffect reset
  }

  return (
    <div>
      <TimePickerContainer>
        <InputV2
          value={time.hours || ''}
          onChange={handleHoursChange}
          type="int"
          min={0}
          max={12}
          width="32px"
          placeholder={placeholders.hours}
          error={error}
        />
        <TimeSeparator>:</TimeSeparator>
        <MinutesInput
          value={
            time.minutes !== null
              ? time.minutes.toString().padStart(2, '0')
              : ''
          }
          onChange={handleMinutesChange}
          type="int"
          min={0}
          max={59}
          width="32px"
          placeholder={placeholders.minutes}
          error={error}
        />
        <AMPMToggle error={error}>
          <AMPMButton
            active={time.period === 'AM'}
            onClick={() => handlePeriodChange('AM')}
            error={error}
          >
            AM
          </AMPMButton>
          <AMPMButton
            active={time.period === 'PM'}
            onClick={() => handlePeriodChange('PM')}
            error={error}
          >
            PM
          </AMPMButton>
        </AMPMToggle>
      </TimePickerContainer>
      {error && (
        <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{error}</div>
      )}
    </div>
  )
}

export default TimePickerV2
